import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export const Eyebrow = ({ children, dark = false, className = "" }) => (
  <p className={`text-[10px] sm:text-[11px] uppercase tracking-[0.35em] font-medium ${dark ? "text-[#C7A45D]" : "text-[#4B0F1E]"} ${className}`}>
    {children}
  </p>
);

export const GoldRule = ({ className = "" }) => (
  <div className={`h-px w-14 bg-[#C7A45D] ${className}`} aria-hidden="true" />
);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const CTALink = ({ to, children, dark = false, solid = false, testId }) => {
  const cls = solid
    ? dark
      ? "bg-[#F7F1E7] text-[#111111] hover:bg-[#C7A45D]"
      : "bg-[#1c1713] text-[#F7F1E7] hover:bg-[#4B0F1E]"
    : dark
    ? "border border-[#C7A45D]/60 text-[#C7A45D] hover:bg-[#C7A45D] hover:text-[#111111]"
    : "border border-[#1c1713]/70 text-[#1c1713] hover:bg-[#1c1713] hover:text-[#F7F1E7]";
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-block px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${cls}`}
    >
      {children}
    </Link>
  );
};

/* Page hero for inner pages — ivory editorial by default, dark cinematic optional */
export const PageHero = ({ eyebrow, title, lede, dark = false, children }) => (
  <section
    className={`${dark ? "bg-[#111111] text-[#F7F1E7]" : "bg-[#F7F1E7] text-[#1c1713]"} px-6 sm:px-10 xl:px-16 pt-40 pb-16 sm:pb-24`}
    data-testid="page-hero"
  >
    <div className="max-w-4xl">
      <Reveal>
        {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
        <h1 className="hcc-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] mt-6">{title}</h1>
        {lede && (
          <p className={`mt-7 text-base font-light leading-relaxed max-w-2xl ${dark ? "text-[#F7F1E7]/70" : "text-[#1c1713]/70"}`}>
            {lede}
          </p>
        )}
        <GoldRule className="mt-9" />
        {children}
      </Reveal>
    </div>
  </section>
);

export const Section = ({ children, dark = false, tint = false, className = "" }) => (
  <section
    className={`px-6 sm:px-10 xl:px-16 py-16 sm:py-24 ${
      dark ? "bg-[#111111] text-[#F7F1E7]" : tint ? "bg-[#efe7d9] text-[#1c1713]" : "bg-[#F7F1E7] text-[#1c1713]"
    } ${className}`}
  >
    {children}
  </section>
);

export const SectionTitle = ({ eyebrow, title, dark = false }) => (
  <Reveal>
    {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
    <h2 className="hcc-serif text-3xl sm:text-4xl font-medium leading-tight mt-4 max-w-2xl">{title}</h2>
    <GoldRule className="mt-7 mb-10" />
  </Reveal>
);
